import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { consume } from '@lit/context';
import {
  publicLedgerServiceContext,
  nsLoaderContext,
  nsSettingsServiceContext,
  ltnDialogServiceContext,
} from '../../../../../ns-services/contexts.js';

import { timeOut } from '../../../../../ltn-helpers/utils/async.js';
import { Debouncer } from '../../../../../ltn-helpers/utils/debouncer.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import { PublicLedgerService } from '../../../../../ns-services/ns-public-ledger-service.js';
import { NSSettingsService, SettingsObject } from '../../../../../ns-settings/ns-settings-service.js';
import { NSLoader } from '../../../../../ns-services/ns-loader-service.js';
import { LtnDialogService } from '../../../../../ltn-dialog/ltn-dialog-service.js';

import { callLambda } from '../../../../../helpers/lambda.js';
import { collectHMRCDeviceInfo } from '../../../../../helpers/fraud-prevention.js';
import { isRegistrar } from '../../../../../helpers/registrars.js';

import Company, { CompaniesRegistrar } from '../../../../../models/company.js';
import Workflow from '../../../../../models/workflow.js';
import type Job from '../../../../../models/job.js';
import JobSchema from '../../../../../models/job-schema.js';

import { renderFormErrorSpan } from '../../../../../helpers/dom.js';
import { FinalFormController, FormConfig } from '../../../../../ns-form/final-form-controller.js';

import NSJobComponent from '../../../job.js';
import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';

import '../../../../../ns-elements/ns-sign-button.js';
import '../../../../../ns-elements/ns-warning-tip.js';

import { TargetBusiness, HMRCJobError, extractVerifiedBusiness, businessMatchesRegistrar } from './validation-logic.js';
import {
  WORDING,
  deriveStage,
  parseVatNameReview,
  pastNext,
  validationOutcome,
  type HmrcStage,
  type VatNameReview,
} from './trust-score-logic.js';
import {
  NSHmrcTrustScoreStep,
  confirmNotMtd,
  postTrustScore,
  renderScoreBar,
  renderScoreInfo,
  type NoVatAnswer,
} from './trust-score-step.js';
import { jobErrorStore } from '../../../../../ns-services/ns-job-error-store.js';
import { rememberOAuthStart } from '../../../../../views/oauth/oauth-landing.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-env-picker.js';
import '../../../../../ns-elements/ns-registrar-mismatch-warning.js';
import '../../../../../ns-elements/ns-registrar-record.js';
import '../../../../../ns-elements/ns-test-credentials.js';
import { canPickEnvironment, defaultEnvironment } from '../../../../../helpers/app-mode.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/hmrc/validation.json';
import { jobSchema } from '../../../job-schema.js';

interface FormValues {
  VRN: string;
}

// HMRC's sandbox test organisation (from HMRC's Create Test User service, with the mtd-vat service), shown on the card off
// production. The sandbox sign-in asks for its VAT number, as the token is granted for the VAT number signed in with.
const SANDBOX_TEST_USER = {
  companyName: 'Company T8M9VR',
  userId: '494849177051',
  password: '5mDUdM5q88p9',
  vrn: '344753642',
};

// Complete records the validation on nodeStream's own public ledger topic, which nodeStream sends and pays for: nobody
// signs it
const SIGN_DESCRIPTION =
  "This records your company's HMRC validation on nodeStream's public ledger and completes this job. No one else needs to sign it.";

// The validation workflow's metadata naming its public ledger message, which Complete (hmrc/record-validation) queues
const LEDGER_TRANSACTION_KEY = 'HMRC_PL_TRANSACTION';

@customElement(NSCompanyHMRCValidation.is)
export default class NSCompanyHMRCValidation extends NSJobComponent {
  static get is() {
    return 'ns-company-hmrc-validation';
  }

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: publicLedgerServiceContext })
  private __pLedgerService?: PublicLedgerService;

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @consume({ context: ltnDialogServiceContext })
  private __dialog?: LtnDialogService;

  @query('ns-hmrc-trust-score-step')
  private __scoreStep?: NSHmrcTrustScoreStep;

  private __company?: Company;

  private __workflow?: Workflow;

  private __debounceCheckHMRCbusiness?: Debouncer;

  private __createFormController: FinalFormController<FormValues>;

  @query('#vat-registered')
  private __vatRegistered?: WaRadioGroup;

  @state()
  private __isVATRegistered: boolean = false;

  @state()
  private __appMode?: string;

  @state()
  private __selectedEnvMode?: string;

  @state()
  private __targetBusiness?: TargetBusiness;

  @state()
  private __companyDetailsMatch: boolean = true;

  // The card is past Next: its HMRC registrar entry is validated (pastNext on load, set by Next itself after)
  @state()
  private __pastNext: boolean = false;

  // This workflow has its public ledger message (Complete recorded it), so the job completes with `complete` rather than
  // `skip`, and the bar's Complete only completes the job
  @state()
  private __ledgerRecorded = false;

  private __connectionOverlayShown = false;

  @state()
  private __completing = false;

  @state()
  private __vatNameReview: VatNameReview | null = null;

  // HMRC answered "not authorised" at the last sign-in (HMRC_NOT_MTD): not signed up for MTD, or the wrong account
  @state()
  private __notMtdRecorded: boolean = false;

  private __hmrcEntry?: CompaniesRegistrar;

  private __errors: Partial<Record<keyof FormValues, string>> = {};

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      ns-test-credentials {
        --ns-test-credentials-gap: var(--wa-space-xs);
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' }, skip: { type: 'hidden' } } });
  }

  constructor() {
    super();

    this.__createFormController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__hmrcLogin(values),
      validate: () => {
        const errors: Partial<Record<keyof FormValues, string>> = {};
        return errors;
      },
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._directory) throw new Error('Unable to find NSDirectoryService');

    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

    if (!this.__settings) throw new Error('Unable to find settings service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__loader) throw new Error('Loader service not found');

    if (!this._job) throw new Error('No job available');

    await this._directory.initialised;

    if (!this._job.companyId) throw new Error('Could not find company linked to this company validation job');

    if (!this._job.workflow || !this._job.workflow.id) throw new Error('Missing required job workflow ID');
    this.__workflow = await this._db.getById('workflow', this._job.workflow.id);
    if (!this.__workflow) throw new Error(`Could not fetch a workflow with ID: ${this._job.workflow.id}`);

    this.__company = await this.loadJobCompany();
    this.__hmrcEntry = this.__company.companiesRegistrar?.find((entry) => isRegistrar(entry, 'HMRC'));
    // Next validated the entry; Complete queues this workflow's ledger message, so a company validated by an earlier card
    // still records one for this card
    this.__ledgerRecorded = !!this.__workflow.metadata?.some((m) => m.key === LEDGER_TRANSACTION_KEY && !!m.value);
    this.__notMtdRecorded = !!this.__metadata('HMRC_NOT_MTD');
    this.__pastNext = pastNext({
      ledgerRecorded: this.__ledgerRecorded,
      entryValidated: !!this.__hmrcEntry?.validated,
      entryProvenId: this.__hmrcEntry?.provenId,
      parkedVrn: this.__metadata('PROVEN_VRN'),
      verifiedVrn: extractVerifiedBusiness(this._job.metadata)?.vatNumber,
      notMtdVrn: this.__metadata('HMRC_NOT_MTD'),
    });

    // App settings
    const app = this.__settings.settings.app as SettingsObject;
    if (!app) throw new Error('Unable to find app settings');
    this.__appMode = app.mode as string;
    this.__selectedEnvMode = defaultEnvironment(this.__appMode, 'LIVE', 'SANDBOX');

    this.__checkHMRCbusiness();

    this._subscriptions.subscribe(`job.${this._job.id}.*`, () => this.__checkHMRCbusiness());
  }

  private async __proceed() {
    if (!this._job) throw new Error('Missing required job');
    this.clearError();

    if (!this.__vatRegistered) throw new Error('Missing required vat registered question');
    const answer = this.__vatRegistered.value;

    // Not VAT registered: HMRC scores 0 with "Not registered for VAT", which still lets the company publish
    if (answer === 'NO') return this.__completeWithNoVat('NOT_REGISTERED');

    if (answer !== 'YES') throw new Error('Not supported answer');

    this.__isVATRegistered = true;
  }

  private async __checkHMRCbusiness() {
    this.__debounceCheckHMRCbusiness = Debouncer.debounce(
      this.__debounceCheckHMRCbusiness,
      timeOut.after(50),
      async () => {
        if (!this._job) throw new Error('Missing job');

        // Live from the job's realtime updates: support's decision lands here while the card is open
        this.__vatNameReview = parseVatNameReview(this.__metadata('VAT_NAME_REVIEW'));
        this.__notMtdRecorded = !!this.__metadata('HMRC_NOT_MTD');
        // The user said yes to VAT and signed in, and HMRC answered: past the VAT question
        if (this.__notMtdRecorded) this.__isVATRegistered = true;

        const targetBusiness = extractVerifiedBusiness(this._job.metadata);
        if (!targetBusiness) return this.__getBusinessCallError();
        if (!this.__company) throw new Error('Missing required company entity');

        this.__targetBusiness = targetBusiness;
        this.__companyDetailsMatch = businessMatchesRegistrar(targetBusiness, this.__company.companiesRegistrar);
        this.__isVATRegistered = true;

        // The connection overlay once, when the card is ready for Next
        if (this.__stage !== 'sign' || this.__connectionOverlayShown) return this.__getBusinessCallError();

        this.__connectionOverlayShown = true;
        this.openCompletionOverlay({
          title: 'HMRC Connection Complete',
          paragraphs: [
            `Your organisation is now securely connected to HMRC. nodeStream can verify company tax status and obligations directly at source,
              helping keep financial workflows accurate, compliant, and auditable.`,
            'Access is read-only and remains fully under your control.',
          ],
        });
      },
    );
  }

  // A metadata value, '' when absent (the lambdas blank a value rather than remove it)
  private __metadata(key: string): string {
    return this._job?.metadata.find((m) => m.key === key)?.value ?? '';
  }

  private get __stage(): HmrcStage {
    return deriveStage({
      kind: 'validation',
      jobComplete: this._job?.status === 'COMPLETE',
      registrarValidated: this.__pastNext,
      businessVerified: !!this.__targetBusiness,
      vatNameReview: this.__vatNameReview,
    });
  }

  // "No VAT" before HMRC has proved a VAT number: the lambda stores HMRC 0 with the reason, then the card completes
  private async __completeWithNoVat(noVat: NoVatAnswer) {
    if (!this._job || !this._job.companyId) throw new Error('Missing job');
    if (noVat === 'NOT_MTD' && !(await confirmNotMtd(this.__dialog))) return;

    this.clearError();
    this.loading(noVat === 'NOT_MTD' ? 'Recording that the business is not signed up for MTD' : 'Recording no VAT');
    try {
      const answer = await postTrustScore(this._auth?.token, {
        jobId: this._job.id,
        companyId: this._job.companyId,
        noVat,
      });
      this.loaded();
      if (answer.code !== 200 || answer.status !== 'OK') {
        this.showError(
          typeof answer.message === 'string' && answer.message ? answer.message : WORDING.notRegisteredFailed,
        );
        return;
      }
      await this.outcome(validationOutcome(this.__ledgerRecorded));
    } catch (err) {
      this.loaded();
      this.showError(err, WORDING.notRegisteredFailed);
    }
  }

  // Complete, once the score step has answered, whatever it said: records the HMRC validation on the public ledger
  // (hmrc/record-validation, once per workflow), then completes the job. A "not signed up for MTD" confirmed at the score
  // step included: the ledger records the HMRC verification Next made, not the score.
  private async __scoreComplete() {
    if (this.__completing || !this._job) return;
    this.__completing = true;
    this.clearError();
    try {
      if (!this.__ledgerRecorded) {
        if (!this._auth) throw new Error('Missing auth service');
        if (!this._job.companyId) throw new Error('Missing job company');
        this.loading("Recording your company's HMRC validation on nodeStream's public ledger");
        try {
          await callLambda(
            'hmrc/record-validation',
            'POST',
            this._auth.token,
            JSON.stringify({ companyId: this._job.companyId, jobId: this._job.id }),
          );
        } finally {
          this.loaded();
        }
        this.__ledgerRecorded = true;
      }
      await this.outcome(validationOutcome(this.__ledgerRecorded));
    } catch (err) {
      this.showError(err, 'This card could not be completed. Please try again.');
    } finally {
      this.__completing = false;
    }
  }

  private async __getBusinessCallError() {
    if (!this._job) throw new Error('Missing job');

    const error = jobErrorStore.get<HMRCJobError>(this._job.id);
    if (error === undefined) return;
    // Back from a sign-in: past the VAT question, so the sign-in shows with the error
    this.__isVATRegistered = true;

    let message = error.message;
    if (error.retryable) {
      message += ' - \n This looks like a temporary issue with HMRC — please try signing in again.';
    }

    if (error.reauthRequired) {
      message += ' - \n Your HMRC authorisation needs to be renewed — please sign in to HMRC again.';
    }

    this.triggerCardError(message);
  }

  private async __hmrcLogin(values: Partial<FormValues>) {
    this.__errors = {};
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__loader) throw new Error('Missing loader service');
    if (!this.__settings) throw new Error('Missing settings service');
    if (!this._job) throw new Error('Missing job');

    this.clearError();

    if (!values.VRN) {
      this.__errors.VRN = 'Please enter a valid company VRN';
      throw new Error('Can not oauth to HMRC without entering a company VRN');
    }

    try {
      await this.__loader.startLoading();
      const body = {
        jobId: this._job.id,
        selectedEnv: this.__selectedEnvMode,
        VRN: values.VRN,
        deviceInfo: collectHMRCDeviceInfo(),
      };

      const LambdaResponse = await callLambda('hmrc/auth', 'POST', this._auth.token, JSON.stringify(body));
      const res = LambdaResponse.res as unknown as { url: string };
      // A declined sign-in comes back to this card (view-oauth)
      rememberOAuthStart('hmrc', this._job.id);
      window.location.href = res.url;
    } catch (err) {
      this.__loader.finishLoading();
      this.triggerCardError(err instanceof Error ? err.message : 'Unable to start HMRC sign-in');
    }
  }

  // Next: hmrc/add-company validates the company's HMRC registrar entry with the grant parked on the job, then the card
  // moves to its score step. The public ledger message waits for Complete.
  private async __next() {
    try {
      this.loading('Validating your company with HMRC');

      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__company) throw new Error('Missing nodeStream company data');
      if (!this.__targetBusiness) throw new Error('Missing HMRC company data');
      if (!this._job) throw new Error('Missing job info');

      this.clearError();

      // Next moves the parked grant off the job, so what it proved is read first
      const provenId = this.__metadata('PROVEN_VRN') || this.__hmrcEntry?.provenId || null;
      const environment =
        (this.__metadata('GRANT_ENVIRONMENT') as CompaniesRegistrar['environment']) ||
        this.__hmrcEntry?.environment ||
        null;

      await callLambda(
        'hmrc/add-company',
        'POST',
        this._auth.token,
        JSON.stringify({ companyId: this.__company.id, jobId: this._job.id }),
      );

      // The score step follows from Next's own result: the entry is now validated with the VAT number HMRC proved
      this.__hmrcEntry = {
        ...(this.__hmrcEntry ?? ({ name: 'HMRC' } as CompaniesRegistrar)),
        validated: true,
        provenId,
        environment,
      };
      this.__pastNext = true;
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err, 'Unable to validate your company with HMRC. Please try again.');
    }
  }

  private __renderHMRCLoginForm() {
    const { form, register } = this.__createFormController;
    const formState = form.getState();

    return html`
      <form
        id="hmrc-vrn-form"
        class="layout vertical"
        @submit=${(e: CustomEvent) => {
          e.preventDefault();
          form.submit();
        }}
      >
        <ns-input-field stacked required ${register('VRN')} placeholder="Enter Here" label="VRN*"></ns-input-field>
        ${renderFormErrorSpan('VRN', this.__errors)}
      </form>
      <div>
        <wa-button variant="brand" type="submit" form="hmrc-vrn-form" ?disabled=${formState.submitting}>
          Sign in to HMRC
        </wa-button>
      </div>
      <p>You'll be securely redirected to HMRC to approve read-only access</p>
    `;
  }

  private __renderCompany() {
    return html`
      <div class="job-panel">
        <ns-registrar-record
          heading="HMRC Data"
          intro="Below is the record retrieved from HMRC. This data reflects the current state of your organisation on HMRC"
          .fields=${[
            { label: 'Vat Registration No:', value: this.__targetBusiness?.vatNumber },
            { label: 'Registered Company Name:', value: this.__targetBusiness?.name },
            { label: 'Registered Address:', value: this.__targetBusiness?.address.line1 },
            { label: 'Registered Post Code:', value: this.__targetBusiness?.address.postcode },
          ]}
        >
          <ns-registrar-mismatch-warning
            slot="status"
            registrar="HMRC"
            ?hidden=${this.__companyDetailsMatch}
          ></ns-registrar-mismatch-warning>
        </ns-registrar-record>
      </div>
    `;
  }

  private __renderEnvForm() {
    return html`
      <ns-env-picker
        service="HMRC"
        live-label="Live HMRC API"
        sandbox-label="Sandbox HMRC API"
        .value=${this.__selectedEnvMode ?? ''}
        @env-change=${(e: CustomEvent<{ value: string }>) => (this.__selectedEnvMode = e.detail.value)}
      ></ns-env-picker>
    `;
  }

  private __renderTestData() {
    return html`
      <div class="job-panel">
        <ns-test-credentials
          .rows=${[
            { label: 'Company Name', value: SANDBOX_TEST_USER.companyName },
            { label: 'User ID', value: SANDBOX_TEST_USER.userId },
            { label: 'Password', value: SANDBOX_TEST_USER.password },
            { label: 'VAT Registration Number', value: SANDBOX_TEST_USER.vrn, digits: true },
          ]}
        >
          <div class="medium mb-2"><strong>HMRC Data</strong></div>
          <div class="small mb-2">Below is the test data for HMRC</div>
        </ns-test-credentials>
      </div>
    `;
  }

  renderVRNForm() {
    return html`
      <div class="job-panel">
        <h3>Before we connect to HMRC</h3>
        <p>To connect your business with HMRC, we'll first need your VAT registration number.</p>
        <p>
          If your organisation is VAT registered, we'll use this to identify your business before you securely authorise
          the connection with HMRC.
        </p>
        <wa-radio-group
          id="vat-registered"
          value="YES"
          orientation="horizontal"
          label="Is your company VAT registered?"
        >
          <wa-radio value="YES">Yes</wa-radio>
          <wa-radio value="NO">No</wa-radio>
        </wa-radio-group>
        <p class="note">
          If your business isn't VAT registered, don't worry. You can still complete the job. However, please note that
          because we cannot verify non-VAT registered businesses with HMRC, your organisation's Trust Score will be
          lower.
        </p>
      </div>
    `;
  }

  private __renderSignIn() {
    return html`
      ${canPickEnvironment(this.__appMode) ? this.__renderEnvForm() : nothing}
      ${this.__selectedEnvMode === 'SANDBOX'
        ? html`
            <div>
              <wa-button
                variant="brand"
                type="submit"
                @click="${() => this.__hmrcLogin({ VRN: SANDBOX_TEST_USER.vrn })}"
              >
                Sign in to HMRC
              </wa-button>
            </div>
            <p>You'll be securely redirected to HMRC to approve read-only access</p>
          `
        : this.__renderHMRCLoginForm()}
    `;
  }

  renderContent() {
    return html`
      <div class="job-panel">
        <h3>Trusted data reduces risk and admin</h3>
        <p>
          Connecting to HMRC helps ensure financial activity on nodeStream is accurate, compliant, and auditable. It
          removes the need for repeated checks, reduces manual reporting, and gives collaborators confidence that
          records reflect real, verified information.
        </p>
        <p>
          The result is smoother payments, clearer accountability, and fewer delays caused by uncertainty or missing
          data.
        </p>
        ${when(!this.__targetBusiness, () => this.__renderSignIn())}
      </div>
      ${when(
        !this.__targetBusiness,
        () => (this.__selectedEnvMode === 'SANDBOX' ? this.__renderTestData() : nothing),
        () => this.__renderCompany(),
      )}
    `;
  }

  // What to have ready, and what the connection does: beside both steps
  renderInfo() {
    return html`
      <div slot="aside" class="primary-info">
        <span class="info-icon" aria-hidden="true">i</span>
        <h3>Before you start</h3>
        <p>
          Connecting takes a few minutes. Have your VAT registration number to hand, along with the sign-in details for
          your business's HMRC online account (usually a Government Gateway user ID and password).
        </p>
        <h3>What this connection enables</h3>
        <ul class="ticks">
          <li>Verifies your company's tax status directly with HMRC</li>
          <li>Confirms obligations and deadlines automatically</li>
          <li>Keeps financial workflows aligned with official records</li>
          <li>Reduces manual checks and duplicate data entry</li>
          <li>Creates an auditable trail for compliance and reporting</li>
        </ul>
        <h3>What access is limited to</h3>
        <ul class="ticks">
          <li>Read-only access</li>
          <li>High-level status and verification data only</li>
          <li>No submission or amendment of returns</li>
          <li>No payroll or employee-level data</li>
          <li>No ability to move money or make payments</li>
        </ul>
        <h3>What stays in your control</h3>
        <ul class="ticks">
          <li>You approve access via HMRC</li>
          <li>Access can be revoked at any time</li>
          <li>nodeStream never sees your HMRC login details</li>
          <li>Data is used only within authorised workflows</li>
        </ul>
      </div>
    `;
  }

  // Reload fallback for the VAT-name review: reads the job again in case a realtime update was missed
  private async __checkReviewAgain() {
    if (!this._db || !this._job) return;
    this.clearError();
    try {
      const fresh = (await this._db.getById('job', this._job.id)) as { metadata?: Job['metadata'] } | undefined;
      if (fresh?.metadata) this._job.metadata = fresh.metadata;
      this.__checkHMRCbusiness();
    } catch (err) {
      this.showError(err, 'Unable to check the review. Please try again.');
    }
  }

  // HMRC answered "not authorised" at the sign-in: sign in again with the right account, or say the business is not
  // signed up for Making Tax Digital (HMRC 0, still publishable)
  private __renderNotMtd() {
    return html`
      <div class="job-panel">
        <h3>${WORDING.reconnectHeading}</h3>
        <p>${WORDING.reconnectOptions}</p>
        <div>
          <wa-button variant="brand" appearance="outlined" @click=${() => this.__completeWithNoVat('NOT_MTD')}>
            Not signed up for MTD
          </wa-button>
        </div>
      </div>
    `;
  }

  private __renderVatNameReview() {
    const review = this.__vatNameReview;
    if (!review) return nothing;
    if (review.status === 'PENDING') {
      return html`
        <div class="job-panel" role="status">
          <h3>${WORDING.vatNamePending}</h3>
          <p>${WORDING.vatNamePendingDetail}</p>
          <div>
            <wa-button variant="brand" appearance="outlined" @click=${() => this.__checkReviewAgain()}>
              Check again
            </wa-button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="job-panel">
        <h3>VAT number not approved</h3>
        <p>${WORDING.vatNameRejected}</p>
        <p>${WORDING.vatNameRejectedAction}</p>
      </div>
    `;
  }

  // The score step, at 'score' and still at 'done': one template, so Lit keeps the step and its answer when the job completes
  private __renderScoreStep(completed: boolean) {
    return html`
      <ns-hmrc-trust-score-step
        kind="validation"
        .jobId=${this._job?.id ?? ''}
        .companyId=${this._job?.companyId ?? ''}
        .provenId=${this.__hmrcEntry?.provenId ?? null}
        .environment=${this.__hmrcEntry?.environment ?? null}
        .appMode=${this.__appMode ?? null}
        ?not-mtd-recorded=${this.__notMtdRecorded}
        ?completed=${completed}
        @score-state-change=${() => this.requestUpdate()}
      ></ns-hmrc-trust-score-step>
    `;
  }

  private __renderBody(stage: HmrcStage) {
    // Completing from the score step keeps its view; a card completed before Next (no VAT, not signed up for MTD) keeps its own
    if (stage === 'score' || (stage === 'done' && this.__pastNext)) return this.__renderScoreStep(stage === 'done');
    if (stage === 'vat-name-pending')
      return html`
        ${this.__renderVatNameReview()}${this.__renderCompany()}
      `;
    // A rejection asks for a sign-in with the right business, so the sign-in shows again under it
    if (stage === 'vat-name-rejected') {
      return html`
        ${this.__renderVatNameReview()}
        <div class="job-panel">${this.__renderSignIn()}</div>
      `;
    }
    if (!this.__isVATRegistered) return this.renderVRNForm();
    return html`
      ${!this.__targetBusiness && this.__notMtdRecorded ? this.__renderNotMtd() : nothing} ${this.renderContent()}
    `;
  }

  render() {
    const stage = this.__stage;
    return html`
      <ns-job-layout>
        <p slot="intro">
          Linking your organisation to
          <span class="underline">HM Revenue & Customs</span>
          allows nodeStream to verify tax and reporting status directly at source. This supports secure financial
          workflows and ensures transactions and records are aligned with official HMRC data.
        </p>
        ${this.__renderBody(stage)}
        ${stage === 'score' || (stage === 'done' && this.__pastNext) ? renderScoreInfo() : this.renderInfo()}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const stage = this.__stage;
    if (stage === 'done' || stage === 'vat-name-pending' || stage === 'vat-name-rejected') return nothing;
    if (stage === 'score') {
      // The ledger message is recorded but the job did not complete (a failure, or a reload): Complete only completes it
      if (this.__ledgerRecorded) {
        return html`
          <wa-button
            variant="brand"
            type="button"
            ?disabled=${this.__completing}
            @click=${() => this.__scoreComplete()}
          >
            Complete
          </wa-button>
        `;
      }
      return renderScoreBar(this.__scoreStep, () => this.__scoreComplete(), {
        completing: this.__completing,
        sign: SIGN_DESCRIPTION,
      });
    }
    return this.__isVATRegistered
      ? html`
          <wa-button variant="brand" type="button" ?disabled=${stage !== 'sign'} @click=${() => this.__next()}>
            Next
          </wa-button>
        `
      : html`
          <wa-button variant="brand" type="button" @click=${() => this.__proceed()}>Continue</wa-button>
        `;
  }
}
